/**
 * SC-04 — append paragraphs to a slide's speaker notes inside an exported
 * single-slide package, leaving every existing paragraph exactly as it was.
 *
 * Deckorate's `writeNotesText` rebuilds the notes part from plain text. That
 * suits its panel, which edits the whole text, but read-then-rebuild loses what
 * the plain text cannot carry — the bold, bullets and blank lines of notes the
 * user wrote. SC-04 only ever ADDS, so it appends `<a:p>` paragraphs to the
 * notes body instead, and uses the rebuild only for a slide with no notes page
 * to append to (there is nothing to lose there).
 *
 * The notes body is the shape whose placeholder is `type="body"`, as DI-02 reads
 * it (notesFromSlidePackage).
 */

import { strFromU8, strToU8 } from 'fflate';
import type { PptxZip } from '../../deckorate/features/apply-brand/services/brandApplier';
import {
  extractNotesText,
  writeNotesText,
} from '../../deckorate/features/SpeakerNotes/services/speakerNotesOffice';
import { resolveRelationshipTarget } from '../../deckorate/utils/ooxmlRelationships';

const NS_P = 'http://schemas.openxmlformats.org/presentationml/2006/main';
const NS_A = 'http://schemas.openxmlformats.org/drawingml/2006/main';
const XML_PROLOG = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';
const SLIDE_PART = /^ppt\/slides\/slide\d+\.xml$/;

/** `existing` notes, a blank line, then `addition` — or `addition` alone. */
export function appendNotes(existing: string, addition: string): string {
  const kept = existing.trimEnd();
  return kept ? `${kept}\n\n${addition}` : addition;
}

/** The `p:txBody` of the notes body placeholder, or null when there is none. */
function notesBody(doc: Document): Element | null {
  for (const sp of Array.from(doc.getElementsByTagNameNS(NS_P, 'sp'))) {
    const ph = sp.getElementsByTagNameNS(NS_P, 'ph')[0];
    if (ph?.getAttribute('type') !== 'body') continue;
    return sp.getElementsByTagNameNS(NS_P, 'txBody')[0] ?? null;
  }
  return null;
}

/** One notes paragraph; an empty line is an empty paragraph (a blank line). */
function paragraph(doc: Document, line: string): Element {
  const p = doc.createElementNS(NS_A, 'a:p');
  if (!line) return p;
  const run = doc.createElementNS(NS_A, 'a:r');
  const props = doc.createElementNS(NS_A, 'a:rPr');
  props.setAttribute('lang', 'en-US');
  props.setAttribute('dirty', '0');
  const t = doc.createElementNS(NS_A, 'a:t');
  t.textContent = line;
  run.append(props, t);
  p.appendChild(run);
  return p;
}

/**
 * `notesXml` with `text` appended to its notes body: after one blank paragraph
 * when the body already has text, in place of its empty paragraphs when it has
 * none. Returns null when the part cannot be parsed or has no notes body.
 */
export function appendToNotesXml(notesXml: string, text: string): string | null {
  const doc = new DOMParser().parseFromString(notesXml, 'text/xml');
  if (doc.getElementsByTagName('parsererror').length > 0) return null;
  const body = notesBody(doc);
  if (!body) return null;

  const existing = Array.from(body.getElementsByTagNameNS(NS_A, 'p'));
  const hasText = Array.from(body.getElementsByTagNameNS(NS_A, 't')).some((t) => t.textContent?.trim());
  if (hasText) body.appendChild(paragraph(doc, ''));
  else for (const p of existing) p.remove();
  for (const line of text.split('\n')) body.appendChild(paragraph(doc, line));

  const serialized = new XMLSerializer().serializeToString(doc);
  return serialized.startsWith('<?xml') ? serialized : XML_PROLOG + serialized;
}

/** Path of the notes part linked from the package's slide, or null. */
function notesPartPath(files: PptxZip): string | null {
  const slidePath = Object.keys(files).find((name) => SLIDE_PART.test(name));
  if (!slidePath) return null;
  const rels = files[`ppt/slides/_rels/${slidePath.slice('ppt/slides/'.length)}.rels`];
  return resolveRelationshipTarget(rels ? strFromU8(rels) : null, '/notesSlide', 'ppt/slides');
}

/**
 * Append `text` to the speaker notes of the slide in `files` (an unzipped
 * single-slide export), in place. Existing paragraphs are kept untouched; a
 * slide with no notes page gets a new one.
 */
export function appendToSlideNotes(files: PptxZip, text: string): void {
  const path = notesPartPath(files);
  const notes = path ? files[path] : undefined;
  const appended = path && notes ? appendToNotesXml(strFromU8(notes), text) : null;
  if (path && appended !== null) {
    files[path] = strToU8(appended);
    return;
  }
  writeNotesText(files, appendNotes(extractNotesText(files), text));
}
